/*
 * All the user-facing wording for "View As", in one place.
 *
 * Same rule as Admin/Progression/help.js: the owner (a maths teacher, not an
 * engineer) will read this on staging and ask for changes. Whoever edits her
 * wording afterwards should only ever need to edit THIS FILE -- never a
 * component. Components import the key they need and never hard-code the words.
 *
 * It lives in consts/ rather than beside either component because two very
 * different places share it: the button and dialog in Admin -> Users, and the
 * banner that sits over every screen in the app.
 *
 * The wording is role-neutral on purpose. Every non-admin account can be viewed
 * as -- children, adult students and parents alike -- so copy that said "this
 * student" was simply wrong in front of a parent's account. Say "this account"
 * and it is true of all of them.
 *
 * The one exception is the banner's consequence line, and only because the
 * server now says which kind of account it is (`viewing_as.user_kind`). Where it
 * says so, the banner names it; where it does not, the role-neutral sentence
 * above is still what she reads. See banner.consequence below.
 *
 * The confirm wording is doing real safeguarding work, not decoration. It has
 * to say three true things that people get wrong about impersonation:
 *
 *   1. It is not a preview. The session becomes that account's.
 *   2. It is not per-tab. Every tab in this browser is that account.
 *   3. Anything clicked counts for them, permanently.
 *
 * See ../wicked-maths-laravel/docs/VIEW_AS_USER.md.
 */

import { VIEW_AS_KIND_PARENT, VIEW_AS_KIND_STUDENT } from "../utils/viewAs"

/*
 * Mirrors App\Models\User::ROLE_ADMIN in ../wicked-maths-laravel. The admin user
 * list returns `role`, and an admin can never be viewed as (the server refuses
 * it), so the button is hidden on those rows rather than offered and then
 * refused.
 *
 * This is the whole rule now: every account except an admin gets the control,
 * which is exactly what the server allows. Deliberately "is NOT an admin"
 * rather than "is a student": if `role` ever goes missing from the payload the
 * button still appears and the server decides, which is the safe way round for
 * a control an admin is looking for.
 */
export const ADMIN_ROLE = 10

/*
 * The consequence line, in its three readings.
 *
 * Two things are load-bearing in every one of them, and neither can be dropped
 * to make the sentence shorter: what she does is written to THEIR record, and
 * she is not herself while she does it. The only thing that varies is whose
 * record it is said to be.
 *
 * GENERIC is the floor, not a failure. It is the sentence this banner shipped
 * with and it is true of every non-admin account, which is why an unknown or
 * absent `user_kind` is allowed to land here and stop. Naming the kind is a
 * refinement on a correct sentence; see viewAsKindOf in utils/viewAs.js for why
 * a missing field falls back to it rather than defaulting to "student".
 */
const CONSEQUENCE_GENERIC = "Everything you do is recorded against this account, not yours."
const CONSEQUENCE_STUDENT = "Everything you do is recorded against this student's account, not yours."
const CONSEQUENCE_PARENT = "Everything you do is recorded against this parent's account, not yours."

export const VIEW_AS = {
	button: {
		label: "View As",
		// Icon-only in the table, so the accessible name carries the account.
		ariaLabel: name => `View the app as ${name}`,
	},

	confirm: {
		heading: name => `View the app as ${name}?`,
		lines: [
			"You will be signed in as this account until you stop. You will see exactly what they see, " +
				"and you will not be an admin again until you press Stop viewing as.",
			"This applies to your whole browser, not just this tab. Every tab you have open on this site " +
				"becomes this account as soon as it reloads. You cannot rely on being yourself in one tab and " +
				"them in another.",
			"Looking is safe. Reading their lessons, modules and messages will not mark their messages as " +
				"read, will not use up their exam request, will not add lesson openings to their reports, " +
				"and will not make them look like they were online.",
			"The one thing looking can do is open work that was already due to them. That is on purpose, so " +
				"that what you see is what they would see. It can only open work, never close it, and it " +
				"opens nothing their own next visit would not have opened anyway.",
			"But anything you click counts as them. A GOT IT or a Reveal Answer records against this account " +
				"and stays on their record, exactly as if they had done it themselves.",
			"The session ends on its own after two hours, and also if they sign in themselves. " +
				"If that happens you will be signed out and will need to sign in again, so stop when you " +
				"are done rather than leaving it open.",
			"Starting and stopping is recorded in their login history against your name.",
		],
		cancel: "Cancel",
		confirm: name => `Yes, view as ${name}`,
		failed: "Could not start viewing as this account. Nothing has changed. Please try again.",
	},

	/*
	 * Shown when a View As session ends underneath the admin rather than
	 * because they pressed Stop: the two-hour expiry ran out, or the account's
	 * owner logged in and revoked it. Their own session went with it -- the SPA
	 * only ever held the other account's token by then -- so signing in again is
	 * the honest instruction, and saying nothing would drop them on the login
	 * screen with no explanation.
	 */
	sessionEnded: "Your View As session has ended. Please sign in again.",

	banner: {
		// Never colour alone: the word "Viewing as" and an icon both carry it.
		viewing: name => `Viewing as ${name}`,
		/*
		 * Takes the kind from viewAsKindOf (utils/viewAs.js), which has already
		 * reduced whatever the payload held to one of the two known words or to
		 * null. Compared against those words rather than looked up by key, so a
		 * value that somehow arrives here unvalidated cannot select a sentence:
		 * anything that is not one of the two lands on the generic line.
		 *
		 * It used to say "as this student" unconditionally, which is false in
		 * front of a parent's account -- the account where the wrong noun does
		 * the most damage, because a message sent from there lands as the
		 * parent, not the child.
		 */
		consequence: kind => {
			if (kind === VIEW_AS_KIND_STUDENT) return CONSEQUENCE_STUDENT
			if (kind === VIEW_AS_KIND_PARENT) return CONSEQUENCE_PARENT

			return CONSEQUENCE_GENERIC
		},
		stop: "Stop viewing as",
		stopping: "Stopping…",
		failed: "Could not stop. You are still signed in as them. Please try again.",
	},

	/*
	 * "Re-check now", pressed from inside the View As session
	 * (../wicked-maths-laravel/docs/SCHEDULED_WORK.md §10).
	 *
	 * ## Why this control is on the banner at all
	 *
	 * An automatic release is written by exactly two things: the account's own
	 * content read, and an hourly sweep. Neither is hers to make happen. So when
	 * she has just changed something -- lifted a lock, pulled a course back,
	 * re-sorted a module, unticked a key module -- and is looking at a child who
	 * ought to have moved, her only options were to wait for a child who may be
	 * asleep, or to wait for the top of the hour.
	 *
	 * It belongs HERE rather than on the admin screen because View As is how she
	 * checks whether a lesson group is really locked, and until this button
	 * existed the fix was one screen away and unreachable from where she stood.
	 *
	 * ## What this comment used to say, and why it changed
	 *
	 * It used to say View As "deliberately suppresses the automatic unlock so
	 * that merely LOOKING at a child's course cannot move their progression", and
	 * that the consequence was View As showing MORE locked than reality. That
	 * suppression was removed in laravel PR #188 on the owner's instruction: "we
	 * are going to let the view as do writes too, no need to artificially hold
	 * back function options".
	 *
	 * So a plain read inside a View As session now opens what is already due, and
	 * the screen tells the truth without this button. The button stays, because it
	 * is still the only way to ask on demand rather than by browsing, and because
	 * its count is the one place she is told how much moved. What has gone is the
	 * bias: View As no longer shows a padlock on work the child would find open.
	 * The confirm wording above changed with it -- it used to promise that looking
	 * "will not unlock anything for them", which stopped being true.
	 *
	 * ## The sentence that makes it safe to press
	 *
	 * `reassurance` is not decoration. A re-check can only OPEN work, never close
	 * it; it can do nothing the account's own next read would not; and it is
	 * idempotent, so pressing twice opens nothing the second time. That is what
	 * turns this from a button she is wary of into one she presses freely, so it
	 * is on screen beside the button rather than buried in help.
	 *
	 * ## "Areas", and the noun in the label
	 *
	 * What opens is a lesson group on a course at group grain and a single LESSON
	 * on one at lesson grain, and the endpoint covers every course the account is
	 * on, so it can be both at once. "Areas" is the server's own word for that
	 * (ViewAsController::recheck) and the only neutral one.
	 *
	 * The LABEL says "this account", and it started out saying "this child".
	 * Worth recording why it changed, because "child" is the more natural word
	 * for what she is usually doing and somebody will want it back.
	 *
	 * Every non-admin account can be viewed as, parents included, so "this child"
	 * was false in front of a parent's account: the same error
	 * `banner.consequence` above was already fixed for, and this file's opening
	 * comment makes role-neutrality the rule for the whole banner. The label was
	 * the one sentence here that broke it. The results and the errors below go
	 * further and carry NO noun for the account at all, which is why nothing but
	 * the label itself had to change.
	 */
	recheck: {
		label: "Check what this account can open now",
		running: "Checking…",

		/*
		 * Said always, under the button. Short enough to be read rather than
		 * skipped, and it carries the two facts that matter: nothing can close,
		 * and pressing again is free.
		 */
		reassurance: "This can only open work, never close it. Pressing it twice opens nothing the second time.",

		/*
		 * The answer, from `areas_opened`.
		 *
		 * ZERO IS GOOD NEWS AND MUST NOT LOOK LIKE A FAILURE. It is the common
		 * answer -- it means the rules have already given this account everything
		 * they are going to -- and reading it as an error is how she would learn
		 * to distrust a button that had just told her the truth. So it is the same
		 * positive line as the others, and it says what it means rather than
		 * showing a 0.
		 */
		opened: count => {
			if (count === 0) return "Nothing new to open. This account already has everything the rules allow."
			if (count === 1) return "Opened 1 new area. Reload the page to see it."

			return `Opened ${count} new areas. Reload the page to see them.`
		},

		/*
		 * OUR OWN COPY, KEYED ON THE HTTP STATUS -- never `error.response.data.message`.
		 *
		 * The lesson is PR #109 in this repo: the sibling billing endpoint is
		 * guarded by a FullOnly middleware whose abort(403, 'This route only for
		 * full accounts') puts a DEVELOPER'S STRING in the same `message` field as
		 * the sentences written for humans, and a component that trusted `message`
		 * would have shown it to a paying parent. Same field, same trap here: this
		 * route is reachable behind a throttle and a statement ceiling, neither of
		 * which phrases anything for her.
		 *
		 * Keeping it here also means she can have any of these reworded without a
		 * developer touching a component, which is the whole rule of this file.
		 *
		 * The statuses are the ones ViewAsController::recheck and its route can
		 * actually produce:
		 *
		 *   422  not viewing as anyone, or the admin account behind the session is
		 *        gone. The session is deliberately LEFT ALONE by the server in both
		 *        cases -- it writes nothing and burns no token -- so the honest
		 *        instruction is Stop, which is the control right next to this one.
		 *   403  the admin who started this session is no longer an admin, or is no
		 *        longer whitelisted. Same shape, same way out.
		 *   429  the throttle. Keyed on the account being viewed, so it is her own
		 *        presses and nobody else's.
		 *   503  the statement ceiling. NOT HER FAULT AND NOT A MISTAKE: the shared
		 *        database was busy, the whole evaluation rolled back, and nothing
		 *        was opened. Pressing it again shortly is the right advice, so the
		 *        sentence says so instead of sounding like a refusal.
		 *
		 * Anything else -- a network drop, a 500, a proxy -- lands on `fallback`,
		 * which says the one thing that is true of all of them: nothing changed.
		 */
		errors: {
			422:
				"This View As session has already ended, so nothing was checked. " +
				"Press Stop viewing as, then start again.",
			403: "Your admin access has changed, so nothing was checked. Press Stop viewing as, then sign in again.",
			429: "That has been pressed a few times already. Wait a moment, then press it again.",
			503: "The database was busy, so nothing was opened. Press it again in a minute.",
			fallback: "Could not check just now, and nothing has changed. Please try again.",
		},
	},

	/*
	 * Shown beside a billing control that has been disabled because a View As
	 * session is live. The server refuses these outright, so the choice on the
	 * client is between a control that silently fails and one that says why --
	 * and the posture on this product is to inform rather than to hide. A
	 * control that vanishes teaches her nothing; this teaches her the rule once.
	 */
	billingBlocked: "Not available while viewing as someone else.",

	/*
	 * The same rule on My Account: saving it (PUT /api/me) would change a real
	 * account's name, email or password, so the server refuses it during View
	 * As (NotWhileViewingAs in ../wicked-maths-laravel). The form stays on
	 * screen, disabled, so she can still see the account.
	 */
	accountEditBlocked: "Not available while viewing as someone else.",

	/*
	 * And on the push notification devices on My Account: adding this browser
	 * would send that account's notifications to the admin's own device.
	 */
	pushBlocked: "Not available while viewing as someone else.",
}

/**
 * The sentence to show when a re-check failed, chosen by HTTP status.
 *
 * Takes the whole axios error so no component has to know where the status
 * lives, and so the one rule -- never render what the server put in `message`
 * -- is enforced in the one place rather than trusted to each caller. A network
 * failure has no `response` at all and lands on the fallback, which is correct:
 * nothing was opened then either.
 *
 * Deliberately a lookup on OUR table with a fallback, rather than a lookup with
 * the server's message as the default. A status this table does not know about
 * is exactly the case where the server's wording is least likely to have been
 * written for her.
 */
export const recheckErrorFor = error => {
	const status = error && error.response ? error.response.status : null

	return VIEW_AS.recheck.errors[status] || VIEW_AS.recheck.errors.fallback
}

export default VIEW_AS
